import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, useField, useFormState } from 'react-final-form'
import { Box, Flex, Text } from '@chilipiper/design-system'
import { Button, Loader, TimezoneSelect } from '@chilipiper/design-system/src/new'
import { IconArrowLeft, IconArrowRight } from '@chilipiper/icons/figma-generated/src/icon'
import {
  ScheduleTimezone,
  WorkingHours as WorkingHoursType,
} from '@chilipiper/api-client-user-availability'
import { OnboardingChiliPiperLogo, Progress } from '@chilipiper/onboarding'
import { getClientTimezone } from '@chilipiper/date-time'
import { routes } from '../../../routes/routes'
import { Link } from '../../../components/link/Link'
import { Spacer } from '../components/Spacer'
import { useIsDesktop } from '../useIsDesktop'
import { useOnboardingExtraSteps } from '../steps'
import { useTrackFeature } from '@/hooks/useTracking'
import { AvailabilityFormWorkingHours } from '@/components/availability-schedules/working-hours/AvailabilityFormWorkingHours'
import {
  AvailabilitySchedule,
  useAvailabilitySchedules,
  usePublishAvailabilityScheduleDraft,
  useUpdateAvailabilityScheduleDraft,
  useUserSyncedTimezone,
} from '@/api/user-availability'
import { TimezoneSelector } from '@/components/availability-schedules/form/TimezoneSelector'
import { ChilicalEvent, ChilicalFeature } from '@/tracking/events'

export const WorkingHours = () => {
  const isDesktop = useIsDesktop()
  const extraSteps = useOnboardingExtraSteps()
  const navigate = useNavigate()
  const track = useTrackFeature()
  const { data: availabilitySchedules = [] } = useAvailabilitySchedules(true)
  const { data: syncedTimezone } = useUserSyncedTimezone()
  const defaultAvailabilitySchedule = availabilitySchedules.find(schedule => schedule.isDefault)

  const { mutateAsync: update } = useUpdateAvailabilityScheduleDraft(
    defaultAvailabilitySchedule?.id ?? ''
  )
  const { mutateAsync: publish } = usePublishAvailabilityScheduleDraft(
    defaultAvailabilitySchedule?.id ?? ''
  )

  const handleSubmit = async (data: { tz: ScheduleTimezone; workingHours: WorkingHoursType }) => {
    const updatedData = {
      ...defaultAvailabilitySchedule?.published?.schedule,
      ...data,
    }
    await update(updatedData)
    await publish(updatedData)
    track(ChilicalEvent.OnboardingWorkingHoursSet, ChilicalFeature.Onboarding)
    navigate(routes.onboarding.location)
  }

  return (
    <Flex
      alignItems='center'
      w='100%'
      h='100%'
      flexDirection='column'
      bg='bg/minor'
      overflow='auto'
      px={6}
      py={5}
      position='relative'
    >
      <OnboardingChiliPiperLogo />
      <Spacer />
      <Flex flexDirection='column' alignItems='center' gap={4} mb={7}>
        <Text fontSize={[20, 20, 32]} fontWeight={600} color='fg/major' textAlign='center'>
          What's your Schedule?
        </Text>
        <Text
          textStyle={isDesktop ? 'body-monoline' : 'body-multiline-tight'}
          color='fg/moderate'
          textAlign='center'
        >
          Set when you're available for meetings
        </Text>
      </Flex>

      <Flex flexDirection='column' maxWidth='648px' w='100%'>
        {!defaultAvailabilitySchedule || syncedTimezone === undefined ? (
          <Flex justifyContent='center' alignItems='center' h={25}>
            <Loader />
          </Flex>
        ) : (
          <Form
            onSubmit={handleSubmit}
            initialValues={{
              tz:
                syncedTimezone === null
                  ? { type: 'FixedTimezone', timezone: getClientTimezone() }
                  : defaultAvailabilitySchedule.published?.schedule.tz,
              workingHours: defaultAvailabilitySchedule.published?.schedule.workingHours,
            }}
            render={({ handleSubmit, values }) => (
              <form onSubmit={handleSubmit}>
                <WorkingHoursFormContent
                  defaultAvailabilitySchedule={defaultAvailabilitySchedule}
                  syncedTimezone={syncedTimezone}
                  values={values}
                />
              </form>
            )}
          />
        )}

        <Progress totalSteps={5 + extraSteps} currentStep={3} />
      </Flex>
      <Spacer />
    </Flex>
  )
}

const WorkingHoursFormContent = ({
  defaultAvailabilitySchedule,
  syncedTimezone,
  values,
}: {
  defaultAvailabilitySchedule: AvailabilitySchedule
  syncedTimezone: string | null
  values: { tz: ScheduleTimezone; workingHours: WorkingHoursType }
}) => {
  const { submitting } = useFormState()
  const { input } = useField<ScheduleTimezone>('tz')

  return (
    <>
      <Box mb={4} mt={1}>
        <AvailabilityFormWorkingHours
          labelPosition='hidden'
          availabilitySchedule={defaultAvailabilitySchedule}
          values={values}
        />
      </Box>
      {syncedTimezone === null ? (
        <TimezoneSelect
          label={<TimezoneSelect.Label position='top'>Timezone</TimezoneSelect.Label>}
          value={input.value.type === 'FixedTimezone' ? input.value.timezone : undefined}
          onChange={value => input.onChange({ type: 'FixedTimezone', timezone: value })}
          variant='regular'
        />
      ) : (
        <TimezoneSelector
          value={input.value}
          onChange={input.onChange}
          syncedTimezone={syncedTimezone}
        />
      )}
      <Flex justifyContent='space-between' mt={7} mb={9}>
        <Link to={routes.onboarding.index}>
          {linkProps => (
            <Button
              variant='minor'
              startContent={<Button.StartContent variant='icon' icon={IconArrowLeft} />}
              as='a'
              {...linkProps}
            >
              Back
            </Button>
          )}
        </Link>
        <Button
          variant='major'
          type='submit'
          isLoading={submitting}
          endContent={<Button.EndContent variant='icon' icon={IconArrowRight} />}
          data-test-id='working-hours-next-button'
        >
          Next
        </Button>
      </Flex>
    </>
  )
}
